import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import {
  CollectionBulkUpdateRequest,
  CollectionItemMoveResult,
  CollectionSummary,
  MAX_COLLECTION_MOVE_ITEMS,
  MoveCollectionItemsRequest,
  SharedViewScope,
  parseCollectionBulkUpdateRequest,
  parseMoveCollectionItemsRequest,
} from '@qkindex/contracts';
import { functions } from '../../firebase';
import { Item } from '../../domain/item';
import { createOperationId } from '../../domain/operationId';
import { firebaseCollectionRepository } from '../../repositories/collectionRepository';
import { useSharedCollectionInventory } from '../collection/useSharedCollectionInventory';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogContent } from '../../components/ui/DialogContent';
import { DialogActions } from '../../components/ui/DialogActions';
import { sharedLocations } from './sharingService';
import { useFeedback } from '../feedback/FeedbackProvider';

const transfer = httpsCallable<
  MoveCollectionItemsRequest,
  CollectionItemMoveResult
>(functions, 'moveCollectionItems');
const update = httpsCallable<Record<string, unknown>, unknown>(
  functions,
  'updateSharedViewItems',
);
type PendingMove =
  | { kind: 'location'; request: CollectionBulkUpdateRequest }
  | { kind: 'transfer'; request: MoveCollectionItemsRequest };

export function SharedItemMoveDialog({
  scope,
  userId,
  collectionName,
  items,
  canTransferOut,
  onClose,
  onMoved,
  onProgress,
}: {
  scope: SharedViewScope;
  userId: string;
  collectionName: string;
  tagName: string;
  items: Item[];
  canTransferOut: boolean;
  onClose(): void;
  onMoved(): void;
  onProgress(): void;
}) {
  const { notify } = useFeedback();
  const [target, setTarget] = useState(scope.collectionId);
  const [locationId, setLocationId] = useState('');
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [localLocations, setLocalLocations] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);
  const pending = useRef<PendingMove | undefined>(undefined);
  const [remainingIds, setRemainingIds] = useState(() =>
    items.map((item) => item.id),
  );
  const alive = useRef(true);
  const storageKey = `qkindex.sharedItemMove:${userId}:${scope.collectionId}:${scope.shareId}`;
  const legacyKey = `qkindex.sharedMove:${userId}:${scope.collectionId}:${scope.shareId}:out`;
  const crossing = target !== scope.collectionId;
  const inventory = useSharedCollectionInventory(
    userId,
    crossing && canTransferOut ? [target] : [],
    ['locations'],
  );
  const entry = inventory.entries.find((value) => value.id === target);
  const externalReady =
    entry?.status === 'ready' &&
    entry.summary?.lifecycle === 'active' &&
    ['owner', 'administrator', 'editor'].includes(entry.summary.role) &&
    !entry.offline &&
    entry.locations.confirmedOnline &&
    !entry.locations.failed;
  const locations = crossing
    ? externalReady
      ? entry.locations.records
      : []
    : localLocations;
  const count = pending.current
    ? pending.current.kind === 'transfer'
      ? pending.current.request.itemIds.length
      : pending.current.request.items.length
    : remainingIds.length;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    try {
      const saved = window.sessionStorage.getItem(storageKey);
      const legacy = saved ? null : window.sessionStorage.getItem(legacyKey);
      if (!saved && !legacy) return;
      const value = saved
        ? JSON.parse(saved)
        : { kind: 'transfer', request: JSON.parse(legacy!) };
      let intent: PendingMove;
      if (value.kind === 'transfer') {
        const request = parseMoveCollectionItemsRequest(value.request);
        if (
          request.sourceCollectionId !== scope.collectionId ||
          request.sourceShareId !== scope.shareId ||
          request.sourceLinkToken ||
          request.destinationLinkToken ||
          !canTransferOut
        )
          return;
        intent = { kind: 'transfer', request };
        setTarget(request.destinationCollectionId);
        setLocationId(request.destinationLocationId);
      } else if (value.kind === 'location') {
        const request = parseCollectionBulkUpdateRequest(value.request);
        if (request.collectionId !== scope.collectionId) return;
        intent = { kind: 'location', request };
        setLocationId(String(request.items[0]?.updates.locationId || ''));
      } else return;
      pending.current = intent;
      setLocked(true);
      setError(
        'A previous move needs its result checked. Check move before starting another.',
      );
    } catch {
      setError('The saved move could not be recovered.');
    }
  }, [
    storageKey,
    legacyKey,
    scope.collectionId,
    scope.shareId,
    canTransferOut,
  ]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void sharedLocations(scope)
      .then((result) => {
        if (active) setLocalLocations(result);
      })
      .catch(() => {
        if (active) setError('Unable to load locations in this collection.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [scope]);
  useEffect(() => {
    if (!canTransferOut) return;
    let active = true;
    void (async () => {
      try {
        const all: CollectionSummary[] = [];
        let cursor: string | undefined;
        do {
          const page = await firebaseCollectionRepository.loadSession(
            userId,
            50,
            cursor,
          );
          all.push(...page.availableCollections.items);
          cursor = page.availableCollections.nextCursor;
        } while (cursor && active);
        if (active)
          setCollections(
            all.filter(
              (value) =>
                value.collectionId !== scope.collectionId &&
                value.lifecycle === 'active' &&
                ['owner', 'administrator', 'editor'].includes(value.role),
            ),
          );
      } catch {
        if (active)
          setError(
            'Unable to load other destination collections. You can still move within this collection.',
          );
      }
    })();
    return () => {
      active = false;
    };
  }, [canTransferOut, userId, scope.collectionId]);

  const submit = async () => {
    if (
      busy ||
      !count ||
      count > MAX_COLLECTION_MOVE_ITEMS ||
      (crossing && !canTransferOut)
    )
      return;
    let intent = pending.current;
    if (!intent) {
      const location = locations.find((value) => value.id === locationId);
      if (!location || (crossing && !externalReady)) return;
      intent = crossing
        ? {
            kind: 'transfer',
            request: {
              operationId: createOperationId(),
              sourceCollectionId: scope.collectionId,
              sourceShareId: scope.shareId,
              destinationCollectionId: target,
              destinationLocationId: locationId,
              itemIds: [...remainingIds].sort(),
            },
          }
        : {
            kind: 'location',
            request: {
              operationId: createOperationId(),
              collectionId: scope.collectionId,
              items: items
                .filter((item) => remainingIds.includes(item.id))
                .map((item) => ({
                  id: item.id,
                  expectedRevision: item.revision,
                  updates: { locationId, location: location.name },
                })),
            },
          };
    }
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(intent));
    } catch {
      setError(
        'Enable browser session storage before moving items so the move can be recovered.',
      );
      return;
    }
    pending.current = intent;
    setBusy(true);
    setLocked(true);
    setError('');
    try {
      if (intent.kind === 'transfer') {
        const result = (
          await transfer({
            ...intent.request,
            ...(scope.linkToken ? { sourceLinkToken: scope.linkToken } : {}),
          })
        ).data;
        if (
          result.state === 'processing' ||
          result.entries.some((value) => value.state !== 'moved')
        ) {
          if (alive.current) {
            if (result.state !== 'processing') {
              const remaining = result.entries
                .filter((value) => value.state !== 'moved')
                .map((value) => value.sourceItemId);
              pending.current = undefined;
              setLocked(false);
              setRemainingIds(remaining);
              window.sessionStorage.removeItem(storageKey);
              window.sessionStorage.removeItem(legacyKey);
            }
            setError(
              result.state === 'processing'
                ? 'Move is still processing. Check move to continue the same operation.'
                : 'Some items could not be transferred. Only the remaining items will be retried.',
            );
            onProgress();
          }
          return;
        }
      } else await update({ ...scope, ...intent.request });
      pending.current = undefined;
      window.sessionStorage.removeItem(storageKey);
      window.sessionStorage.removeItem(legacyKey);
      if (alive.current) {
        setLocked(false);
        onMoved();
      }
    } catch (failure) {
      if (
        intent.kind === 'location' &&
        (failure as { details?: { code?: string } })?.details?.code ===
          'operation-conflict'
      ) {
        pending.current = undefined;
        window.sessionStorage.removeItem(storageKey);
        window.sessionStorage.removeItem(legacyKey);
        if (alive.current) {
          setLocked(false);
          onProgress();
          onClose();
          notify(
            'The items changed. Refreshing inventory; select them again to retry the move.',
            'warning',
          );
        }
        return;
      }
      if (alive.current)
        setError(
          failure instanceof Error
            ? failure.message
            : 'Unable to move items. Check move retries the same operation.',
        );
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const close = () => {
    if (!busy) onClose();
  };
  return (
    <ResponsiveDialog
      open
      onClose={close}
      aria-labelledby="shared-items-move-title"
    >
      <DialogHeader
        id="shared-items-move-title"
        title={count === 1 ? 'Move item' : `Move ${count} items`}
        onClose={close}
        closeDisabled={busy}
      />
      <DialogContent>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {count > MAX_COLLECTION_MOVE_ITEMS && (
            <Alert severity="warning">
              Select at most {MAX_COLLECTION_MOVE_ITEMS} items to move.
            </Alert>
          )}
          {canTransferOut && (
            <TextField
              select
              label="Destination collection"
              value={target}
              disabled={busy || locked}
              onChange={(event) => {
                setTarget(event.target.value);
                setLocationId('');
              }}
            >
              <MenuItem value={scope.collectionId}>
                {collectionName} (current collection)
              </MenuItem>
              {locked &&
                crossing &&
                !collections.some((value) => value.collectionId === target) && (
                  <MenuItem value={target}>Previous destination</MenuItem>
                )}
              {collections.map((value) => (
                <MenuItem key={value.collectionId} value={value.collectionId}>
                  {value.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label="Location"
            value={locationId}
            disabled={
              busy ||
              locked ||
              (!crossing && loading) ||
              (crossing && !externalReady)
            }
            onChange={(event) => setLocationId(event.target.value)}
          >
            {locked &&
              locationId &&
              !locations.some((value) => value.id === locationId) && (
                <MenuItem value={locationId}>Previous location</MenuItem>
              )}
            {locations.map((value) => (
              <MenuItem key={value.id} value={value.id}>
                {value.name}
              </MenuItem>
            ))}
          </TextField>
          {(crossing
            ? entry?.status === 'loading' || entry?.locations.busy
            : loading) && (
            <Typography role="status" color="text.secondary">
              Loading locations…
            </Typography>
          )}
          {crossing &&
            (entry?.status === 'error' ||
              entry?.status === 'unavailable' ||
              entry?.locations.failed) && (
              <Alert
                severity="error"
                action={<Button onClick={inventory.retry}>Retry</Button>}
              >
                This destination is unavailable.
              </Alert>
            )}
          {crossing && entry?.locations.nextCursor && (
            <Button
              disabled={entry.locations.busy}
              onClick={() => {
                void inventory.loadMore();
              }}
            >
              Load more locations
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            busy ||
            !count ||
            count > MAX_COLLECTION_MOVE_ITEMS ||
            (!locked && !locations.some((value) => value.id === locationId))
          }
          onClick={() => {
            void submit();
          }}
        >
          {busy
            ? 'Moving…'
            : locked
              ? 'Check move'
              : crossing
                ? 'Transfer items'
                : 'Move Items'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
